import React, {PropsWithChildren, useCallback, useContext, useEffect, useState} from "react";
import {PreviewDataContext} from "./PreviewContext";
import {Wisetack} from "../models/wisetack.model";
import {getWisetackPromoLinkAsync, getWisetackPromoTextAsync} from "../api/WisetackApi";
import {CompanyContext} from "./CompanyContext";
import {InvoiceContext} from "./InvoiceContext";
import {CustomerContext} from "./CustomerContext";

interface WisetackContextState {
    wisetack: Wisetack | null;
    isWisetackLoading: boolean;
    reloadWisetack: (customTotal: number) => void;
}

export const WisetackContext = React.createContext({} as WisetackContextState);

export const WisetackProvider: React.FunctionComponent<PropsWithChildren> = ({children}) => {
    const [wisetack, setWisetack] = useState<Wisetack>({} as Wisetack);
    const [isWisetackLoading, setWisetackLoading] = useState<boolean>(false);
    const previewContext = useContext(PreviewDataContext);
    const company = useContext(CompanyContext);
    const invoice = useContext(InvoiceContext);
    const customer = useContext(CustomerContext);

    const loadWisetackText = async (customTotal: number = -1) => {
        setWisetackLoading(true);
        const wisetackResponse = await getWisetackPromoTextAsync(previewContext.isPreviewMode, customTotal);
        updateWisetack(wisetackResponse);
        setWisetackLoading(false);
    };

    const loadWisetackLink = async (customTotal: number = -1) => {
        setWisetackLoading(true);
        const wisetackResponse = await getWisetackPromoLinkAsync(previewContext.isPreviewMode, customTotal);
        updateWisetack(wisetackResponse);
        setWisetackLoading(false);
    };

    const updateWisetack = useCallback((wisetackResponse: Wisetack) => {
        setWisetack(Object.assign({}, Object.assign(wisetack, wisetackResponse)));
    }, [wisetack, setWisetack]);

    const generateWisetackPreviewText = useCallback((total: number) => {
        let paymentAmount = 0;
        if (total < 5000) {
            paymentAmount = total * 0.04518170;
        } else if (total < 8000) {
            paymentAmount = total * 0.03175321;
        } else {
            paymentAmount = total * 0.02119787;
        }
        const wisetackResponse: Wisetack = {asLowAs: {monthlyPayment: paymentAmount.toFixed(2)}};
        updateWisetack(wisetackResponse);
    }, [company, invoice, updateWisetack]);

    const reloadWisetack = (customTotal: number = 0) => {
        if (!previewContext.isPreviewMode && previewContext.isPreviewInited && company.is_wisetack_enabled && invoice.is_wisetack_available && customer.is_wisetack_available) {
            loadWisetackText(customTotal);
            loadWisetackLink(customTotal);
        } else if (previewContext.isPreviewMode && previewContext.isPreviewInited && company.is_wisetack_enabled && invoice.is_wisetack_available && customer.is_wisetack_available) {
            generateWisetackPreviewText(customTotal || invoice.total_as_number);
        }
    }

    useEffect(() => {
        reloadWisetack();
    }, [previewContext.isPreviewInited, company, invoice, customer]);

    return (
        <WisetackContext.Provider value={{wisetack, isWisetackLoading, reloadWisetack}}>
            {children}
        </WisetackContext.Provider>
    )
}
